/**
 * Folder tree helpers for collections.
 * Folders live in collection.settings.folders; requests use folder_id (mirrored in settings.folder_id for API).
 */
import { protocolFieldsFromSettings, settingsWithoutProtocol } from './requestProtocolFields';

export function generateFolderId() {
  return `fld-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function getFolders(collection) {
  const raw = collection?.settings?.folders;
  return Array.isArray(raw) ? raw.map((f) => ({ ...f })) : [];
}

export function normalizeParentId(parentId) {
  if (parentId == null || parentId === '' || parentId === 'null') return null;
  return String(parentId);
}

export function getFolderById(collection, folderId) {
  return getFolders(collection).find((f) => String(f.id) === String(folderId)) ?? null;
}

/**
 * The folder records enclosing a request, NEAREST FIRST: the folder it sits in,
 * then that folder's parent, up to the collection root. Cycle-safe, and stops
 * at a folder id that does not exist. The ONE walk behind
 * `getFolderAuthChain` and MCP's `request_read` folder chain, so the app's
 * send path and what an agent is told cannot walk different trees.
 */
export function getEnclosingFolders(collection, folderId) {
  const byId = new Map(getFolders(collection).map((f) => [String(f.id), f]));
  const chain = [];
  const seen = new Set();
  let current = normalizeParentId(folderId);
  while (current) {
    if (seen.has(current)) break;
    seen.add(current);
    const folder = byId.get(String(current));
    if (!folder) break;
    chain.push(folder);
    current = normalizeParentId(folder.parent_id);
  }
  return chain;
}

/**
 * Auth objects for the folder chain enclosing a request, nearest folder first.
 * Folders without an `auth` block contribute nothing (they inherit). Used to
 * fold folder-scoped auth between request and collection auth.
 */
export function getFolderAuthChain(collection, folderId) {
  return getEnclosingFolders(collection, folderId)
    .filter((folder) => folder.auth)
    .map((folder) => folder.auth);
}

export function isDescendantFolder(folders, folderId, possibleAncestorId) {
  let current = folderId;
  const seen = new Set();
  while (current) {
    const cur = String(current);
    if (cur === String(possibleAncestorId)) return true;
    if (seen.has(cur)) break;
    seen.add(cur);
    const f = folders.find((x) => String(x.id) === cur);
    current = f?.parent_id ?? null;
  }
  return false;
}

/** Ordered siblings: folders + requests at parentId (null = collection root). */
export function getOrderedChildren(collection, parentId = null) {
  const pid = normalizeParentId(parentId);
  const folders = getFolders(collection)
    .filter((f) => normalizeParentId(f.parent_id) === pid)
    .map((f) => ({
      type: 'folder',
      item: f,
      sort_order: typeof f.sort_order === 'number' ? f.sort_order : 0,
      id: f.id,
    }));

  const requests = (collection.requests || [])
    .filter((r) => normalizeParentId(r.folder_id) === pid)
    .map((r) => ({
      type: 'request',
      item: r,
      sort_order: typeof r.sort_order === 'number' ? r.sort_order : 0,
      id: r.id,
    }));

  return [...folders, ...requests].sort((a, b) => a.sort_order - b.sort_order);
}

export function hydrateCollectionFromApi(collection) {
  if (!collection) return collection;
  return {
    ...collection,
    requests: (collection.requests || []).map(hydrateRequestFromApi),
  };
}

export function hydrateCollectionsFromApi(collections) {
  return (collections || []).map(hydrateCollectionFromApi);
}

export function hydrateRequestFromApi(apiRequest) {
  if (!apiRequest) return apiRequest;
  const folderId =
    apiRequest.folder_id ??
    apiRequest.settings?.folder_id ??
    null;
  const formDataFields = apiRequest.settings?.form_data_fields ?? null;
  return {
    ...apiRequest,
    folder_id: folderId ? String(folderId) : null,
    formDataFields: Array.isArray(formDataFields) ? formDataFields : undefined,
    // Normalize snake_case API fields → camelCase for the frontend model
    preRequestScript: apiRequest.preRequestScript ?? apiRequest.pre_request_script ?? '',
    testScript: apiRequest.testScript ?? apiRequest.test_script ?? '',
    mock: apiRequest.mock ?? apiRequest.settings?.mock,
    // gRPC / WebSocket fields (and `type`) come back from settings (requestProtocolFields.js);
    // the packed copy is dropped so it cannot go stale next to them.
    ...protocolFieldsFromSettings(apiRequest),
    ...(apiRequest.settings ? { settings: settingsWithoutProtocol(apiRequest.settings) } : {}),
  };
}

export function stripFolderIdForPayload(source) {
  // Use `!== undefined` so an explicit `folder_id: null` (move to root) is not overridden by settings.folder_id.
  const folderId =
    source.folder_id !== undefined ? source.folder_id : (source.settings?.folder_id ?? null);
  const settings = { ...(source.settings || {}) };
  if (folderId) {
    settings.folder_id = String(folderId);
  } else {
    delete settings.folder_id;
  }
  const formFields = source.formDataFields ?? source.settings?.form_data_fields;
  const bodyType = source.body_type || source.bodyType;
  const rawLang = source.rawBodyLanguage ?? source.settings?.raw_body_language;
  if (bodyType === 'formdata' || bodyType === 'form-data') {
    delete settings.raw_body_language;
  } else if (rawLang) {
    settings.raw_body_language = rawLang;
  } else {
    delete settings.raw_body_language;
  }
  // URL-encoded bodies are rows too (the editor and the runner read
  // `form_data_fields` for both, see multipart.js). Deleting them here for
  // urlencoded meant a cloud workspace — whose only copy of a request is this
  // payload, sent with `body: ''` — lost every urlencoded field on import and
  // on save (found 28.09.2026 importing a Postman token request).
  const isUrlencoded = bodyType === 'urlencoded' || bodyType === 'form' || bodyType === 'x-www-form-urlencoded';
  if ((bodyType === 'formdata' || isUrlencoded) && Array.isArray(formFields) && formFields.length > 0) {
    settings.form_data_fields = formFields
      .filter((row) => row?.key?.trim())
      .map((row) => ({
        key: row.key,
        value: row.value ?? '',
        type: !isUrlencoded && row.type === 'file' ? 'file' : 'text',
        enabled: row.enabled !== false,
        filePath: isUrlencoded ? null : row.filePath ?? null,
      }));
  } else if (bodyType !== 'formdata' && !isUrlencoded) {
    delete settings.form_data_fields;
  }
  return { folder_id: folderId ? String(folderId) : null, settings };
}

export function withCollectionFolders(collection, folders) {
  return {
    ...collection,
    settings: {
      ...(collection.settings || {}),
      folders,
    },
  };
}

export function createFolderInCollection(collection, { name, parent_id = null }) {
  const folders = getFolders(collection);
  const pid = normalizeParentId(parent_id);
  const siblings = getOrderedChildren(collection, pid);
  const maxOrder = siblings.reduce((m, s) => Math.max(m, s.sort_order), -1);
  const folder = {
    id: generateFolderId(),
    name: name || 'New Folder',
    parent_id: pid,
    sort_order: maxOrder + 1,
  };
  return withCollectionFolders(collection, [...folders, folder]);
}

export function renameFolderInCollection(collection, folderId, name) {
  const folders = getFolders(collection).map((f) =>
    String(f.id) === String(folderId) ? { ...f, name } : f
  );
  return withCollectionFolders(collection, folders);
}

/** Collect folder ids in subtree (including root folderId). */
export function collectFolderSubtreeIds(folders, folderId) {
  const ids = new Set([String(folderId)]);
  let changed = true;
  while (changed) {
    changed = false;
    for (const f of folders) {
      if (f.parent_id != null && ids.has(String(f.parent_id)) && !ids.has(String(f.id))) {
        ids.add(String(f.id));
        changed = true;
      }
    }
  }
  return ids;
}

export function deleteFolderFromCollection(collection, folderId) {
  const folders = getFolders(collection);
  const removeIds = collectFolderSubtreeIds(folders, folderId);
  const nextFolders = folders.filter((f) => !removeIds.has(String(f.id)));
  const nextRequests = (collection.requests || []).filter(
    (r) => !removeIds.has(String(r.folder_id))
  );
  return {
    ...withCollectionFolders(collection, nextFolders),
    requests: nextRequests,
  };
}

/**
 * Clone a folder subtree (the root folder + every nested folder) with fresh ids.
 * Descendants are re-parented onto their copied parent; the root keeps its original
 * parent but gets `rootSortOrder` and a " Copy" suffix on its name. Leaf rows that
 * live inside the subtree (requests, schema items, …) are remapped by the caller
 * using the returned `idMap`.
 *
 * @param {Array}    folders        flat folder array: { id, name, parent_id, sort_order }
 * @param {*}        folderId       id of the subtree root to clone
 * @param {Object}   opts
 * @param {Function} opts.newFolderId    () => string, generates a fresh folder id
 * @param {number}   opts.rootSortOrder  sort_order to assign the cloned root folder
 * @returns {{ copiedFolders: Array, idMap: Map<string,string>, subtreeIds: Set<string>, rootId: string, rootName: string }}
 */
export function cloneFolderSubtree(folders, folderId, { newFolderId, rootSortOrder }) {
  const subtreeIds = collectFolderSubtreeIds(folders, folderId);
  const idMap = new Map();
  subtreeIds.forEach((id) => idMap.set(String(id), newFolderId()));

  let rootName = '';
  const copiedFolders = folders
    .filter((f) => subtreeIds.has(String(f.id)))
    .map((f) => {
      const isRoot = String(f.id) === String(folderId);
      const parentId = normalizeParentId(f.parent_id);
      const name = isRoot ? `${f.name || 'Folder'} Copy` : f.name;
      if (isRoot) rootName = name;
      return {
        ...f,
        id: idMap.get(String(f.id)),
        // Root re-parents under the original's parent; descendants re-map to the copied parent.
        parent_id: isRoot ? parentId : idMap.get(String(parentId)),
        name,
        sort_order: isRoot ? rootSortOrder : f.sort_order,
      };
    });

  return { copiedFolders, idMap, subtreeIds, rootId: idMap.get(String(folderId)), rootName };
}

/**
 * Duplicate a folder subtree (child folders + requests) as a sibling of the source folder.
 * Returns null if the folder is missing. New request rows use temporary ids for local workspaces.
 */
export function duplicateFolderInCollection(collection, folderId) {
  const folders = getFolders(collection);
  const root = getFolderById(collection, folderId);
  if (!root) return null;

  const pid = normalizeParentId(root.parent_id);

  const { copiedFolders, idMap, subtreeIds, rootId, rootName } = cloneFolderSubtree(
    folders,
    folderId,
    { newFolderId: generateFolderId, rootSortOrder: root.sort_order ?? 0 }
  );

  const copiedRequests = (collection.requests || [])
    .filter((r) => r.folder_id && subtreeIds.has(String(r.folder_id)))
    .map((r, index) => {
      // `stripFolderIdForPayload`, not a bare `folder_id:` — the placement is
      // stored TWICE (`folder_id` and `settings.folder_id`, the form the API
      // persists). Spreading `r` and overwriting only the first left the copy's
      // `settings.folder_id` pointing at the ORIGINAL folder, which is the one
      // the server keeps: after a reload the copy sat in the source folder.
      const { settings, folder_id } = stripFolderIdForPayload({
        ...r,
        folder_id: idMap.get(String(r.folder_id)),
      });
      return {
        ...r,
        id: `dup-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 9)}`,
        folder_id,
        settings,
      };
    });

  let nextCollection = {
    ...withCollectionFolders(collection, [...folders, ...copiedFolders]),
    requests: [...(collection.requests || []), ...copiedRequests],
  };

  nextCollection = positionEntryAfterSource(
    nextCollection,
    pid,
    'folder',
    folderId,
    'folder',
    rootId
  );

  return {
    collection: nextCollection,
    rootFolderId: rootId,
    addedRequests: copiedRequests,
    copyName: rootName,
  };
}

/**
 * A copy of `collection` whose folders all have FRESH ids, with every reference
 * to them (`parent_id`, each request's `folder_id` and `settings.folder_id`)
 * re-pointed. Names, order and nesting are unchanged; request ids are the
 * caller's business.
 *
 * Why "Duplicate collection" needs this: folders are not rows, they are JSON in
 * `collection.settings.folders`, so copying the settings copied the folder IDS
 * too — and the sidebar assumes a folder id names exactly one folder in the
 * whole workspace. Drag targets are `data-drag-id="folder-<id>"`, so a drop
 * indicator lit up in BOTH collections (reported 18.09.2026: "2 indication lines
 * showing even my mouse was not on that position"), and `treeSelection`'s
 * `folderOwner` index is keyed the same way, so a bulk action on a folder in the
 * copy could resolve to the folder in the original.
 *
 * @returns {{ collection: Object, idMap: Map<string,string> }}
 */
export function remapCollectionFolderIds(
  collection,
  { newFolderId = generateFolderId, only = null } = {}
) {
  const folders = getFolders(collection);
  // `only` (a Set of ids) re-ids just those folders — the repair pass below
  // uses it so a collection that clashes on ONE id does not get every folder
  // renamed under it. Everything not in the map is left exactly as it was.
  const idMap = new Map(
    folders
      .filter((f) => !only || only.has(String(f.id)))
      .map((f) => [String(f.id), newFolderId()])
  );
  const partial = Boolean(only);

  const nextFolders = folders.map((f) => {
    const parentId = normalizeParentId(f.parent_id);
    const mappedParent = parentId ? idMap.get(parentId) : null;
    return {
      ...f,
      id: idMap.get(String(f.id)) ?? f.id,
      // Full re-id: a parent that is not in this collection is a dangling
      // reference already, so the folder goes to the root rather than keeping an
      // id that means nothing. Partial: an unmapped parent is simply untouched.
      parent_id: mappedParent ?? (partial ? f.parent_id ?? null : null),
    };
  });

  const requests = (collection.requests || []).map((r) => {
    const current = normalizeParentId(r.folder_id ?? r.settings?.folder_id);
    if (partial && !(current && idMap.has(current))) return r;
    const { settings, folder_id } = stripFolderIdForPayload({
      ...r,
      folder_id: current ? idMap.get(current) ?? null : null,
    });
    return { ...r, folder_id, settings };
  });

  return {
    collection: { ...withCollectionFolders(collection, nextFolders), requests },
    idMap,
  };
}

/**
 * Repair folder ids that appear in MORE THAN ONE collection — the damage
 * "Duplicate collection" did before it called `remapCollectionFolderIds`.
 *
 * The first collection to use an id keeps it; a later one gets fresh ids for
 * just the clashing folders. A collection the user cannot edit is left alone:
 * the repair has to be persisted, and a write they are not allowed to make
 * would be refused and then reloaded, forever.
 *
 * Returns the SAME array when nothing clashes, so callers can compare by
 * identity (it runs on every collections change).
 */
export function dedupeCollectionFolderIds(collections, options = {}) {
  const seen = new Set();
  let changed = false;

  const next = (collections || []).map((col) => {
    const ids = getFolders(col).map((f) => String(f.id));
    const clashing = new Set(ids.filter((id) => seen.has(id)));
    if (clashing.size === 0 || col.can_edit === false) {
      ids.forEach((id) => seen.add(id));
      return col;
    }
    const { collection: repaired } = remapCollectionFolderIds(col, { ...options, only: clashing });
    getFolders(repaired).forEach((f) => seen.add(String(f.id)));
    changed = true;
    return repaired;
  });

  return changed ? next : collections;
}

export function moveRequestToFolder(collection, requestId, folderId) {
  const fid = normalizeParentId(folderId);
  const requests = (collection.requests || []).map((r) => {
    if (String(r.id) !== String(requestId)) return r;
    const { settings, folder_id } = stripFolderIdForPayload({ ...r, folder_id: fid });
    return { ...r, folder_id, settings };
  });
  return { ...collection, requests };
}

export function moveFolderInCollection(collection, folderId, newParentId) {
  const folders = getFolders(collection);
  const fid = String(folderId);
  const pid = normalizeParentId(newParentId);
  if (pid && (String(pid) === fid || isDescendantFolder(folders, pid, fid))) {
    return collection;
  }
  const siblings = getOrderedChildren(
    { ...collection, settings: { ...collection.settings, folders: folders.map((f) => (String(f.id) === fid ? { ...f, parent_id: pid } : f)) } },
    pid
  ).filter((s) => String(s.id) !== fid);
  const maxOrder = siblings.reduce((m, s) => Math.max(m, s.sort_order), -1);
  const nextFolders = folders.map((f) =>
    String(f.id) === fid ? { ...f, parent_id: pid, sort_order: maxOrder + 1 } : f
  );
  return withCollectionFolders(collection, nextFolders);
}

function reindexSiblings(collection, parentId, orderedIds) {
  let folders = getFolders(collection);
  let requests = [...(collection.requests || [])];
  const pid = normalizeParentId(parentId);

  orderedIds.forEach((entry, index) => {
    if (entry.type === 'folder') {
      folders = folders.map((f) =>
        String(f.id) === String(entry.id) ? { ...f, sort_order: index, parent_id: pid } : f
      );
    } else {
      requests = requests.map((r) => {
        if (String(r.id) !== String(entry.id)) return r;
        const { settings, folder_id } = stripFolderIdForPayload({ ...r, folder_id: pid });
        return { ...r, folder_id, sort_order: index, settings };
      });
    }
  });

  return { ...withCollectionFolders(collection, folders), requests };
}

/**
 * Move `entryId` to sit immediately after `sourceId` among siblings under `parentId`.
 * Both entries must already exist in the collection.
 */
export function positionEntryAfterSource(
  collection,
  parentId,
  sourceType,
  sourceId,
  entryType,
  entryId
) {
  const pid = normalizeParentId(parentId);
  const siblings = getOrderedChildren(collection, pid);
  const without = siblings.filter((s) => String(s.id) !== String(entryId));
  const sourceIdx = without.findIndex(
    (s) => s.type === sourceType && String(s.id) === String(sourceId)
  );
  const insertAt = sourceIdx === -1 ? without.length : sourceIdx + 1;
  const reordered = [
    ...without.slice(0, insertAt).map((s) => ({ type: s.type, id: s.id })),
    { type: entryType, id: entryId },
    ...without.slice(insertAt).map((s) => ({ type: s.type, id: s.id })),
  ];
  return reindexSiblings(collection, pid, reordered);
}

/** Append a request copy and place it immediately after the source request. */
export function addRequestDuplicateAfter(collection, sourceRequestId, copyRequest) {
  const src = (collection.requests || []).find(
    (r) => String(r.id) === String(sourceRequestId)
  );
  if (!src) return collection;
  const next = {
    ...collection,
    requests: [...(collection.requests || []), copyRequest],
  };
  return positionEntryAfterSource(
    next,
    normalizeParentId(src.folder_id),
    'request',
    sourceRequestId,
    'request',
    copyRequest.id
  );
}

/**
 * Reorder/move by DnD within collection tree.
 * @param {'folder'|'request'} activeType
 * @param {'folder'|'request'|'collection'} overType
 */
export function applyTreeDrag(collection, activeType, activeId, overType, overId) {
  if (!activeId || !overId) return null;

  if (activeType === 'folder' && overType === 'folder' && String(activeId) === String(overId)) {
    return null;
  }

  let next = collection;

  if (overType === 'collection') {
    if (activeType === 'request') {
      next = moveRequestToFolder(next, activeId, null);
    } else if (activeType === 'folder') {
      next = moveFolderInCollection(next, activeId, null);
    }
    // Append the moved item at the end of root-level items so it lands in a predictable spot.
    const rootItems = getOrderedChildren(next, null);
    const without = rootItems.filter((s) => String(s.id) !== String(activeId));
    const activeEntry = rootItems.find((s) => String(s.id) === String(activeId));
    if (activeEntry) {
      return reindexSiblings(next, null, [...without, activeEntry]);
    }
    return next;
  }

  if (overType === 'folder') {
    const targetFolderId = String(overId);
    if (activeType === 'request') {
      next = moveRequestToFolder(next, activeId, targetFolderId);
    } else if (activeType === 'folder') {
      if (isDescendantFolder(getFolders(next), targetFolderId, activeId)) return null;
      next = moveFolderInCollection(next, activeId, targetFolderId);
    }
    const parentId = activeType === 'request'
      ? targetFolderId
      : getFolderById(next, activeId)?.parent_id ?? null;
    const siblings = getOrderedChildren(next, parentId);
    const without = siblings.filter((s) => String(s.id) !== String(activeId));
    const overIdx = without.findIndex((s) => s.type === 'folder' && String(s.id) === targetFolderId);
    const insertAt = overIdx === -1 ? without.length : overIdx + (activeType === 'request' ? 1 : 0);
    const activeEntry = siblings.find((s) => String(s.id) === String(activeId));
    if (!activeEntry) return next;
    const reordered = [...without.slice(0, insertAt), activeEntry, ...without.slice(insertAt)];
    return reindexSiblings(next, parentId, reordered);
  }

  if (overType === 'request') {
    const targetReq = (collection.requests || []).find((r) => String(r.id) === String(overId));
    if (!targetReq) return null;
    const parentId = normalizeParentId(targetReq.folder_id);

    if (activeType === 'request') {
      next = moveRequestToFolder(next, activeId, parentId);
    } else if (activeType === 'folder') {
      if (parentId && isDescendantFolder(getFolders(next), parentId, activeId)) return null;
      next = moveFolderInCollection(next, activeId, parentId);
    }

    const siblings = getOrderedChildren(next, parentId);
    const without = siblings.filter((s) => String(s.id) !== String(activeId));
    const overIdx = without.findIndex((s) => s.type === 'request' && String(s.id) === String(overId));
    if (overIdx === -1) return next;
    const activeEntry = siblings.find((s) => String(s.id) === String(activeId));
    if (!activeEntry) return next;
    const reordered = [...without.slice(0, overIdx), activeEntry, ...without.slice(overIdx)];
    return reindexSiblings(next, parentId, reordered);
  }

  return null;
}

/** Current parent id (null = root) of a request/folder within the collection. */
function currentParentId(collection, type, id) {
  if (type === 'request') {
    const r = (collection.requests || []).find((x) => String(x.id) === String(id));
    return normalizeParentId(r?.folder_id);
  }
  const f = getFolderById(collection, id);
  return normalizeParentId(f?.parent_id);
}

/**
 * Position-aware DnD drop within a collection tree.
 * Unlike applyTreeDrag (which infers a fixed slot), this honors an explicit
 * `position` relative to the over-target:
 *   - over collection            → drop into collection root (append)
 *   - over folder, 'inside'       → drop into that folder (append as last child)
 *   - over folder, 'before'/'after' → drop as a sibling of the folder
 *   - over request, 'before'/'after' → drop as a sibling of the request
 *
 * @param {'folder'|'request'} activeType
 * @param {'folder'|'request'|'collection'} overType
 * @param {'before'|'after'|'inside'} position
 * @returns {object|null} next collection, or null on no-op / invalid move.
 */
export function applyTreeDropWithPosition(
  collection,
  activeType,
  activeId,
  overType,
  overId,
  position = 'before'
) {
  if (!activeId || !overId) return null;
  if (String(activeId) === String(overId)) return null;

  const folders = getFolders(collection);

  let parentId; // normalized destination parent (null = root)
  let refId = null; // sibling to insert relative to
  let refType = null;
  let insertAfter = false;

  if (overType === 'collection') {
    parentId = null;
  } else if (overType === 'folder') {
    if (position === 'inside') {
      parentId = String(overId);
    } else {
      const folder = getFolderById(collection, overId);
      if (!folder) return null;
      parentId = normalizeParentId(folder.parent_id);
      refId = String(overId);
      refType = 'folder';
      insertAfter = position === 'after';
    }
  } else if (overType === 'request') {
    const targetReq = (collection.requests || []).find((r) => String(r.id) === String(overId));
    if (!targetReq) return null;
    parentId = normalizeParentId(targetReq.folder_id);
    refId = String(overId);
    refType = 'request';
    insertAfter = position === 'after';
  } else {
    return null;
  }

  // A folder may not drop into itself or one of its descendants.
  if (activeType === 'folder' && parentId) {
    if (String(parentId) === String(activeId) || isDescendantFolder(folders, parentId, activeId)) {
      return null;
    }
  }

  const originParent = currentParentId(collection, activeType, activeId);

  let next =
    activeType === 'request'
      ? moveRequestToFolder(collection, activeId, parentId)
      : moveFolderInCollection(collection, activeId, parentId);

  const siblings = getOrderedChildren(next, parentId);
  const without = siblings.filter((s) => String(s.id) !== String(activeId));
  const activeEntry = siblings.find((s) => String(s.id) === String(activeId));
  if (!activeEntry) return next;

  let insertAt;
  if (refId == null) {
    insertAt = without.length;
  } else {
    const idx = without.findIndex((s) => s.type === refType && String(s.id) === String(refId));
    insertAt = idx === -1 ? without.length : idx + (insertAfter ? 1 : 0);
  }

  const reordered = [...without.slice(0, insertAt), activeEntry, ...without.slice(insertAt)];

  // No-op guard: same parent and identical sibling order → nothing changed.
  if (String(originParent ?? '') === String(parentId ?? '')) {
    const beforeIds = getOrderedChildren(collection, parentId).map((s) => String(s.id));
    const afterIds = reordered.map((s) => String(s.id));
    if (beforeIds.length === afterIds.length && beforeIds.every((v, i) => v === afterIds[i])) {
      return null;
    }
  }

  return reindexSiblings(next, parentId, reordered);
}

/** Expand folder ids for ancestors of a request/folder. */
export function ancestorFolderIds(collection, folderId) {
  const folders = getFolders(collection);
  const ids = [];
  // A folder graph is supposed to be a tree, but `settings.folders` arrives
  // verbatim from imported and published workspaces, so `a -> b -> a` is one
  // line in an attacker's file. Without this guard the walk never terminates
  // and, because the callers are the sidebar search filter and expand-on-
  // select, typing a single character into search froze the main thread with
  // no way to recover. `getFolderAuthChain` and `isDescendantFolder` already
  // carry the same `seen` set; this walker was the one that was missed.
  const seen = new Set();
  let current = normalizeParentId(folderId);
  while (current && !seen.has(String(current))) {
    const cur = String(current);
    seen.add(cur);
    ids.push(cur);
    const f = folders.find((x) => String(x.id) === cur);
    current = f ? normalizeParentId(f.parent_id) : null;
  }
  return ids;
}

/** Filter collection for sidebar search — keeps matching requests/folders and ancestors. */
export function filterCollectionForSearch(collection, searchTerm) {
  const q = String(searchTerm || '').trim().toLowerCase();
  if (!q) return collection;

  const folders = getFolders(collection);
  const requests = collection.requests || [];
  const matchingFolderIds = new Set();
  const matchingRequestIds = new Set();

  for (const f of folders) {
    if (f.name?.toLowerCase().includes(q)) matchingFolderIds.add(String(f.id));
  }
  for (const r of requests) {
    if (
      r.name?.toLowerCase().includes(q) ||
      r.url?.toLowerCase().includes(q) ||
      collection.name?.toLowerCase().includes(q)
    ) {
      matchingRequestIds.add(String(r.id));
      if (r.folder_id) {
        for (const aid of ancestorFolderIds(collection, r.folder_id)) {
          matchingFolderIds.add(aid);
        }
      }
    }
  }

  for (const f of folders) {
    if (matchingFolderIds.has(String(f.id)) && f.parent_id) {
      for (const aid of ancestorFolderIds(collection, f.id)) {
        matchingFolderIds.add(aid);
      }
    }
  }

  const keepFolder = (folderId) => {
    if (matchingFolderIds.has(String(folderId))) return true;
    return requests.some(
      (r) =>
        matchingRequestIds.has(String(r.id)) &&
        (String(r.folder_id) === String(folderId) ||
          isDescendantFolder(folders, r.folder_id, folderId))
    );
  };

  const nextFolders = folders.filter((f) => keepFolder(f.id));
  const nextRequests = requests.filter((r) => matchingRequestIds.has(String(r.id)));

  return {
    ...collection,
    settings: { ...(collection.settings || {}), folders: nextFolders },
    requests: nextRequests,
  };
}
